import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import {
  Breadcrumb,
  BreadCrumbItem,
} from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { useAppDispatch } from "../../../../hooks/useTypedSelector";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import useCrudTable from "../../../../hooks/useCrudTable";
import { tireSetSchema } from "../../domain/schemas/tireSetSchema";
import useCrudFilters from "../../../../hooks/useCrudFilters";
import { TireSetsTableFilters } from "../features/tireSetsTableFilters";
import { useTranslation } from "react-i18next";
import { useCrudExport } from "../../../../hooks/useCrudExport";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { useTireSetsTableColumns } from "../../infrastructure/hooks/useTireSetsTableColumns";
import { useEffect, useState, useMemo } from "react";
import { AllRequestQueryResponseModel } from "../../../request/domain/models/requestModel";
import { getAllRequestStates } from "../../../request/infrastructure/slices/requestMutateSlice";
import useEntitySelect from "../../../../hooks/useEntitySelect";
import { relationSchema } from "../../../relation/domain/models/RelationModel";

export const TireSetsPage = () => {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();

  const dispatch = useAppDispatch();

  const title = t("Tire sets");

  const crudFilters = useCrudFilters(
    {
      searchBy: "SearchByRootTireset",
      fields: {},
    },
    "tireSetFilter",
  );

  const addAction = () => {
    if (
      hasRole("dbh-employee") ||
      hasRole("dbh-driver") ||
      hasRole("dbh-order-picker")
    ) {
      return undefined;
    }
    return "/car/add";
  };

  const columnsWithUpdateData = useTireSetsTableColumns();

  // Extract license plate filter order for custom sorting
  const licencePlateOrder = useMemo(() => {
    const carFilter = crudFilters.filters?.fields?.car;
    if (carFilter && "fields" in carFilter) {
      const licenceFilter = carFilter.fields?.licence;
      if (
        licenceFilter &&
        "in" in licenceFilter &&
        Array.isArray(licenceFilter.in)
      ) {
        return licenceFilter.in as string[];
      }
    }
    return null;
  }, [crudFilters.filters]);

  // Custom sorting function for license plates
  const customSortData = useMemo(() => {
    if (!licencePlateOrder) {
      return null;
    }

    return (data: unknown[]) => {
      return [...data].sort((a, b) => {
        const tiresetA = a as { car?: { licence?: string } };
        const tiresetB = b as { car?: { licence?: string } };
        const licenceA = tiresetA?.car?.licence;
        const licenceB = tiresetB?.car?.licence;

        if (!licenceA || !licenceB) return 0;

        const indexA = licencePlateOrder.indexOf(licenceA);
        const indexB = licencePlateOrder.indexOf(licenceB);

        // If both license plates are in the filter order, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one is in the order, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in the order, maintain current order
        return 0;
      });
    };
  }, [licencePlateOrder]);

  const [allRequestStates, setAllRequestStates] = useState<
    AllRequestQueryResponseModel | undefined
  >();

  useEffect(() => {
    if (!allRequestStates) {
      void dispatch(getAllRequestStates())
        .unwrap()
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setAllRequestStates(response);
        });
    }
  }, []);

  const buildPickupStateOptions = () => {
    if (allRequestStates) {
      return allRequestStates.pickupStates.map((state) => {
        return {
          value: state,
          label: state,
        };
      });
    }
  };

  const relationFilters = useMemo(
    () => ({
      searchBy: "SearchByRootRelation",
      fields: {},
    }),
    [],
  );

  const relationSelect = useEntitySelect({
    entity: "Relation",
    prefix: "relations",
    labelPrefix: "name",
    schema: relationSchema,
    page: 1,
    limit: 100,
    filters: relationFilters,
  });

  const bulkFields = useMemo(
    () => [
      {
        field: "sleep_set",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        date: false,
      },
      {
        field: "in_hotel",
        options: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        date: false,
      },
      {
        field: "relation",
        options: relationSelect.options as { label: string; value: string }[],
        loadOptions: relationSelect.loadOptions,
        date: false,
      },
      {
        field: "state",
        options: buildPickupStateOptions() as {
          label: string;
          value: string;
        }[],
        date: false,
      },
      { field: "in_out_storage", options: [], date: true },
    ],
    [relationSelect.options, allRequestStates],
  );

  const crud = useCrudTable({
    prefix: "tiresets",
    entity: "Tireset",
    columns: columnsWithUpdateData,
    schema: tireSetSchema,
    bulkSticker: {
      type: "tireset",
      idField: "id",
    },
    bulkRoles: ["dbh-manager", "super-admin"],
    bulkFields: bulkFields,
    bulkFieldsResponseExclude: ["relation", "state", "in_out_storage"],
    bulkFieldsRequiredGroup: ["state", "in_out_storage"],
    bulkFieldsMessageAboveRequiredGroup: t(
      "Fields below will update latest active pickup request or create a new one.",
    ),
    addAction: addAction(),
    columnAction: "/car/:id/edit",
    filters: crudFilters.filters,
    queryExclude: ["invoice_status"],
    enableCustomSorting: licencePlateOrder != null,
    customSortData: customSortData,
    defaultSorting: licencePlateOrder ? [] : undefined,
    dealerPath: "car.relation", // Path to dealer information in tire set data
  });

  const exportButton = useCrudExport({
    pageCount: crud.tableInstance.getPageCount(),
    queryFunction: "exportTiresets",
    queryData: [
      { car: ["licence"] },
      "type",
      "sleep_set",
      { position: ["label"] },
      {
        storedLatestRequest: [
          { relation: ["name"] },
          "type",
          "state",
          "pickup_and_delivery_date",
          "in_out_storage",
        ],
      },
    ],
    filters: crudFilters.filters,
  });

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <div className="d-flex flex-wrap align-items-center justify-content-between w-100">
            <div>
              <LayoutHeaderTitle title={title} />
            </div>
            <div className="flex-grow-1 mx-md-3 my-2 my-md-0">
              <Breadcrumb>
                <BreadCrumbItem>{title}</BreadCrumbItem>
              </Breadcrumb>
            </div>
            <div className="mt-2 mb-2 mt-md-0">
              <LayoutHeaderActions>
                {crud.addButton}
                {exportButton.exportButton}
              </LayoutHeaderActions>
            </div>
          </div>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader title={title} className="mb-3" />
          <TireSetsTableFilters {...crudFilters} />
          {crud.bulk}
          {crud.table}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
